// The frame's lists (07-frame.md, "Building the lists"): joined rooms and invitations, direct chats,
// each room's parent spaces, the rail's spaces and their order; live from the client's events.
import {
  ClientEvent,
  EventType,
  KnownMembership,
  NotificationCountType,
  PushRuleActionName,
  RoomEvent,
  RoomStateEvent,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { readSettings } from '../core/settings';
import { isJoined, ownReceipt } from '../session/rooms';
import { counts } from '../chat/events';

export const RAIL_EVENT = 'chat.desyr.rail';
const EMOJI_ROOM = 'org.desyr.company_emoji';

export interface Lists {
  joined: Room[];
  invites: Room[];
  /** Direct chats (m.direct), joined. */
  direct: Set<string>;
  /** Room ID → the joined spaces that link it (with `via`). */
  parents: Map<string, Room[]>;
  /** The rail's spaces, in the person's order (chat.desyr.rail), then the rest. */
  rail: Room[];
}

/** A space's own children links with a `via`, in the space's order (07: `order` first, then oldest). */
export function childLinks(space: Room): { roomId: string; order?: string; ts: number }[] {
  return space.currentState
    .getStateEvents(EventType.SpaceChild)
    .filter((e) => Array.isArray(e.getContent().via) && e.getStateKey())
    .map((e) => {
      const order = e.getContent().order;
      return { roomId: e.getStateKey()!, order: typeof order === 'string' && order.length <= 50 ? order : undefined, ts: e.getTs() };
    })
    .sort((a, b) => (a.order && b.order ? (a.order < b.order ? -1 : a.order > b.order ? 1 : a.ts - b.ts) : a.order ? -1 : b.order ? 1 : a.ts - b.ts));
}

export function buildLists(client: MatrixClient, left: Set<string>): Lists {
  const rooms = client.getRooms().filter((r) => !left.has(r.roomId) && r.getType() !== EMOJI_ROOM);
  const joinedRooms = rooms.filter(isJoined);
  const ids = new Set(joinedRooms.map((r) => r.roomId));
  const dm = client.getAccountData(EventType.Direct)?.getContent<Record<string, string[]>>() ?? {};
  const direct = new Set(Object.values(dm).flat().filter((id) => ids.has(id)));
  const parents = new Map<string, Room[]>();
  for (const space of joinedRooms.filter((r) => r.isSpaceRoom()))
    for (const { roomId } of childLinks(space)) parents.set(roomId, [...(parents.get(roomId) ?? []), space]);
  const topSpaces = joinedRooms.filter((r) => r.isSpaceRoom() && !parents.has(r.roomId));
  // The saved order; folders (a candidate to drop, not built) are read flat.
  const saved = (client.getAccountData(RAIL_EVENT as never)?.getContent<{ items?: unknown[] }>()?.items ?? []).flatMap((i) =>
    typeof i === 'string' ? [i] : Array.isArray((i as { spaces?: unknown }).spaces) ? ((i as { spaces: unknown[] }).spaces.filter((s) => typeof s === 'string') as string[]) : [],
  );
  const rank = (r: Room) => {
    const at = saved.indexOf(r.roomId);
    return at < 0 ? saved.length + topSpaces.indexOf(r) : at;
  };
  return {
    joined: joinedRooms,
    invites: rooms.filter((r) => r.getMyMembership() === KnownMembership.Invite),
    direct,
    parents,
    rail: [...topSpaces].sort((a, b) => rank(a) - rank(b)),
  };
}

/** Home's channels: joined, not a space, not direct, in no space; A to Z ("#" and case ignored). */
export const homeChannels = (l: Lists) =>
  l.joined
    .filter((r) => !r.isSpaceRoom() && !l.direct.has(r.roomId) && !l.parents.has(r.roomId))
    .sort((a, b) => a.name.replace(/#/g, '').localeCompare(b.name.replace(/#/g, ''), undefined, { sensitivity: 'base' }));

const activity = (r: Room) => r.getLastActiveTimestamp();

/** Direct chats, the most recently active first. */
export const directChats = (l: Lists) => l.joined.filter((r) => l.direct.has(r.roomId)).sort((a, b) => activity(b) - activity(a));

export interface Category {
  space: Room;
  rooms: Room[];
}

/** A space's list: its own channels, then every space inside it as a category, in tree order. */
export function spaceTree(client: MatrixClient, space: Room): { own: Room[]; categories: Category[] } {
  const seen = new Set<string>([space.roomId]);
  const children = (s: Room) =>
    childLinks(s)
      .map((c) => client.getRoom(c.roomId))
      .filter((r): r is Room => isJoined(r) && r.getType() !== EMOJI_ROOM);
  const categories: Category[] = [];
  const walk = (s: Room) => {
    for (const sub of children(s).filter((r) => r.isSpaceRoom())) {
      if (seen.has(sub.roomId)) continue;
      seen.add(sub.roomId);
      const rooms = children(sub).filter((r) => !r.isSpaceRoom());
      if (rooms.length) categories.push({ space: sub, rooms });
      walk(sub);
    }
  };
  walk(space);
  return { own: children(space).filter((r) => !r.isSpaceRoom()), categories };
}

/** Every room inside a space, at any depth (for its rail dot and "Mark as read"). */
export function roomsIn(client: MatrixClient, space: Room): Room[] {
  const { own, categories } = spaceTree(client, space);
  return [...own, ...categories.flatMap((c) => c.rooms)];
}

export type Mode = 'default' | 'all' | 'mentions' | 'mute';
const notifies = (actions: unknown[] = []) => actions.includes(PushRuleActionName.Notify);

/** A room's notification mode from the account's push rules (12, rule 10). */
export function modeOf(client: MatrixClient, roomId: string): Mode {
  const rules = client.pushRules?.global;
  const muted = rules?.override?.some((r) => {
    const first = r.conditions?.[0] as { kind?: string; key?: string; pattern?: string } | undefined;
    return r.enabled !== false && !notifies(r.actions) && (r.rule_id === roomId || (first?.kind === 'event_match' && first.key === 'room_id' && first.pattern === roomId));
  });
  if (muted) return 'mute';
  const rule = rules?.room?.find((r) => r.rule_id === roomId && r.enabled !== false);
  return rule ? (notifies(rule.actions) ? 'all' : 'mentions') : 'default';
}

/** Rooms where the person's own receipt arrived in this session (12, rules 7 and 8). */
const receiptSeen = new Set<string>();
export const sawOwnReceipt = (roomId: string) => receiptSeen.add(roomId);

export interface Unread {
  count: number;
  /** Unread without a number (12, rule 3). */
  dot: boolean;
  red: boolean;
  mentions: number;
}
const NONE: Unread = { count: 0, dot: false, red: false, mentions: 0 };

/** What counts in a room (12, rules 1–8): the server's numbers, capped by the app's own count once its receipt was seen. */
export function unread(room: Room, isDirect: boolean): Unread {
  const client = room.client;
  const me = client.getUserId()!;
  if (modeOf(client, room.roomId) === 'mute') return NONE;
  const highlight = room.getUnreadNotificationCount(NotificationCountType.Highlight);
  const total = Math.max(room.getUnreadNotificationCount(NotificationCountType.Total), highlight);
  // The app's own count: counting events from others after the read position (one's own newest message counts as read).
  const events = room.getLiveTimeline().getEvents();
  const receipt = ownReceipt(room, me);
  const read = Math.max(receipt ? events.findIndex((e) => e.getId() === receipt) : -1, events.findLastIndex((e) => e.getSender() === me));
  const app = read >= 0 ? events.slice(read + 1).filter((e) => counts(e, me)).length : undefined;
  const made = (count: number, mentions: number, dot = false): Unread => ({ count, dot, mentions, red: (count > 0 || dot) && (isDirect || mentions > 0) });
  if (receiptSeen.has(room.roomId) && app !== undefined) {
    const count = Math.min(total, app);
    return count ? made(count, Math.min(highlight, count)) : NONE;
  }
  if (total > 0) return made(total, highlight);
  return app && events[events.length - 1]?.getSender() !== me ? made(0, 0, true) : NONE;
}

/** Anything unread among the rooms ("Mark as read" is disabled otherwise). */
export const anyUnread = (rooms: Room[]) => rooms.some((r) => { const u = unread(r, false); return u.count > 0 || u.dot; });

/** The circle's channels, in its spaces too: every joined room but spaces and direct chats. */
export const allChannels = (l: Lists) => l.joined.filter((r) => !r.isSpaceRoom() && !l.direct.has(r.roomId));

/** The unread mentions and replies in the circle's channels: Home's tab and its Mentions row on a phone (07, v1.58.59). */
export const channelMentions = (l: Lists) => allChannels(l).reduce((n, r) => n + unread(r, false).mentions, 0);

/** A section's dot on the rail: none, "dot" or "red". */
export function dotFor(rooms: Room[], l: Lists): 'dot' | 'red' | undefined {
  let any = false;
  for (const r of rooms) {
    const u = unread(r, l.direct.has(r.roomId));
    if (u.red) return 'red';
    any ||= u.count > 0 || u.dot;
  }
  return any ? 'dot' : undefined;
}

/** Marks rooms as read on their newest event not being sent (07, "Marking as read"). */
export async function markAsRead(client: MatrixClient, rooms: Room[]): Promise<void> {
  const type = readSettings()['private-activity'] ? 'm.read.private' : 'm.read';
  for (const room of rooms) {
    const events = room.getLiveTimeline().getEvents();
    const newest = [...events].reverse().find((e: MatrixEvent) => !e.isSending() && e.getId()?.startsWith('$'));
    if (newest && ownReceipt(room, client.getUserId()!) !== newest.getId())
      await client.sendReadReceipt(newest, type as never).catch(() => {});
  }
}

/** The lists, kept current by the client's events (one rebuild per frame at most). */
export function useLists(client: MatrixClient, left: Set<string>): Lists {
  const [lists, setLists] = useState(() => buildLists(client, left));
  useEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setLists(buildLists(client, left)));
    };
    update();
    const events = [ClientEvent.Room, ClientEvent.DeleteRoom, ClientEvent.AccountData, RoomEvent.MyMembership, RoomEvent.Name, RoomEvent.Timeline, RoomEvent.UnreadNotifications, RoomEvent.Receipt, RoomStateEvent.Events] as const;
    const emitter = client as unknown as { on(e: string, f: () => void): void; off(e: string, f: () => void): void };
    events.forEach((e) => emitter.on(e, update));
    return () => {
      cancelAnimationFrame(frame);
      events.forEach((e) => emitter.off(e, update));
    };
  }, [client, left]);
  return lists;
}
